import StepYou from "./steps/StepYou";
import StepBusiness from "./steps/StepBusiness";
import StepResidence from "./steps/StepResidence";
import StepOwnership from "./steps/StepOwnership";
import StepEmployment from "./steps/StepEmployment";
import StepEquipment from "./steps/StepEquipment";
import StepReview from "./steps/StepReview";

/**
 * PD-6 step registry.
 *
 * Ordered step definitions keyed by path (business/personal). A step can
 * either share one component across both paths (`sharedComponent`) or swap
 * which component renders per path (`businessComponent` / `personalComponent`).
 *
 * Milestone 1 gave steps 1, 4 and 5 real shared components. Milestone 2
 * fills in steps 2 and 3 with real per-path components (see steps/StepBusiness.js,
 * steps/StepResidence.js, steps/StepOwnership.js, steps/StepEmployment.js) —
 * no path hits a placeholder stub any more.
 *
 * Milestone 4 removes the Offers step again — the application now ends at
 * Review & Sign (StepReview.js already captures the signature and submits),
 * so rate/term/monthly-payment matching is out of scope until there's a real
 * backend offers API. See StepReview.js's own history for the back-and-forth.
 */
const steps = [
  {
    key: "you",
    label: "You",
    sharedComponent: StepYou,
    timeLeft: "~30 sec",
    footNote: "Nothing is submitted yet",
    sidebarNote:
      "Nothing here is submitted yet, and nothing on this page affects your credit score.",
  },
  {
    key: "business-residence",
    businessLabel: "Business",
    personalLabel: "Residence",
    businessComponent: StepBusiness,
    personalComponent: StepResidence,
    businessTimeLeft: "~40 sec",
    personalTimeLeft: "~35 sec",
    footNote: "Nothing is submitted yet",
    businessSidebarNote:
      "Nothing here is submitted yet, and nothing on this page affects your credit score.",
    personalSidebarNote:
      "Nothing here is submitted yet, and nothing on this page affects your credit score.",
  },
  {
    key: "ownership-employment",
    businessLabel: "Ownership",
    personalLabel: "Employment & Income",
    businessComponent: StepOwnership,
    personalComponent: StepEmployment,
    timeLeft: "~45 sec",
    footNote: "Nothing is submitted yet",
    businessSidebarNote:
      "Nothing here is submitted yet, and nothing on this page affects your credit score.",
    personalSidebarNote:
      "Nothing here is submitted yet, and nothing on this page affects your credit score.",
  },
  {
    key: "equipment",
    label: "Equipment",
    sharedComponent: StepEquipment,
    timeLeft: "~30 sec",
    footNote: "Nothing is submitted yet",
    sidebarNote:
      "Nothing here is submitted yet, and nothing on this page affects your credit score.",
  },
  {
    key: "review",
    label: "Review",
    sharedComponent: StepReview,
    timeLeft: "~20 sec",
    // The one step where the generic "nothing submitted yet" line doesn't
    // apply — this step's whole point is the signature, so V3 uses a
    // signature-specific footnote here instead of the uniform one every
    // other step shares.
    footNote: "One signature covers both",
    sidebarNote:
      "Nothing here is submitted yet, and nothing on this page affects your credit score.",
  },
];

export function getStepLabel(step, isPersonalFlow) {
  if (step.label) return step.label;
  return isPersonalFlow ? step.personalLabel : step.businessLabel;
}

export function getStepTimeLeft(step, isPersonalFlow) {
  if (step.timeLeft) return step.timeLeft;
  return isPersonalFlow ? step.personalTimeLeft : step.businessTimeLeft;
}

export function getStepFootNote(step, isPersonalFlow) {
  if (step.footNote) return step.footNote;
  return isPersonalFlow ? step.personalFootNote : step.businessFootNote;
}

export function getStepSidebarNote(step, isPersonalFlow) {
  if (step.sidebarNote) return step.sidebarNote;
  return isPersonalFlow ? step.personalSidebarNote : step.businessSidebarNote;
}

export function getStepComponent(step, isPersonalFlow) {
  if (step.sharedComponent) return step.sharedComponent;
  return isPersonalFlow ? step.personalComponent : step.businessComponent;
}

export default steps;
